import { Check, X, MapPin } from "lucide-react";

export const DataHandling = () => (
  <section id="data" className="py-24 bg-[#F7F7F8]">
    <div className="max-w-[1120px] mx-auto px-6">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#9E1B32] bg-[#FBEAEE] px-3 py-1 rounded-full">Two data buckets</span>
      <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight max-w-[620px]">Old data and new data need different approaches.</h2>

      <div className="mt-12 grid md:grid-cols-2 gap-5">
        <div className="rounded-[14px] bg-white border border-[#E7E7EC] p-8" data-testid="new-data-card">
          <span className="text-xs font-bold uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">New data · The easier part</span>
          <h3 className="mt-4 text-xl font-semibold">Follows DPDP rules from the start</h3>
          <ul className="mt-4 space-y-3 text-[15px] text-[#5C5F6B]">
            {["Clear notice before collection", "Recorded, verifiable consent", "Only what's actually needed", "DP team sign-off before any collection"].map((i) => (
              <li key={i} className="flex gap-2.5"><Check size={17} className="text-green-600 mt-0.5 shrink-0" />{i}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-[14px] bg-white border border-[#E7E7EC] p-8" data-testid="old-data-card">
          <span className="text-xs font-bold uppercase tracking-wider text-[#7A1426] bg-[#FBEAEE] border border-[#F3D3DB] px-2.5 py-1 rounded-full">Old data · The hard part</span>
          <h3 className="mt-4 text-xl font-semibold">Start by deleting it</h3>
          <p className="mt-3 text-[15px] text-[#5C5F6B] leading-relaxed">"We might need it later" doesn't count. To keep anything, teams need a specific reason. Then, in order of preference:</p>
          <ul className="mt-4 space-y-3 text-[15px] text-[#5C5F6B]">
            <li className="flex gap-2.5"><span className="font-bold text-[#9E1B32] shrink-0">1.</span>Analyse it, then delete the raw data (AI makes this quick)</li>
            <li className="flex gap-2.5"><span className="font-bold text-[#9E1B32] shrink-0">2.</span>Anonymise the personal details, keep the dataset usable</li>
            <li className="flex gap-2.5"><span className="font-bold text-[#9E1B32] shrink-0">3.</span>Encrypt person-level data, only as a last resort</li>
          </ul>
        </div>
      </div>

      <div className="mt-5 rounded-[14px] overflow-hidden relative" data-testid="data-india-banner">
        <img
          src="https://images.pexels.com/photos/17489160/pexels-photo-17489160.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Server racks in an Indian data centre"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0B0B0F]/80" />
        <div className="relative p-8 lg:p-12 flex flex-col md:flex-row md:items-center gap-6">
          <span className="w-12 h-12 rounded-[10px] bg-[#F58220] grid place-items-center text-white shrink-0">
            <MapPin size={22} />
          </span>
          <div>
            <h3 className="text-2xl font-bold text-white">The non-negotiable: all data stays in India.</h3>
            <p className="mt-2 text-white/70 text-[15px] max-w-[640px] leading-relaxed">
              Indian beneficiary data shouldn't sit on servers outside India. Move your tools to an India region with
              vendor confirmation in writing, or switch to tools that store data here. No room to compromise.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-[14px] bg-white border border-[#E7E7EC] p-8 flex flex-col md:flex-row gap-6 md:items-start" data-testid="special-category-card">
        <span className="w-11 h-11 rounded-[10px] bg-[#FBEAEE] grid place-items-center text-[#9E1B32] shrink-0"><X size={20} /></span>
        <div>
          <h3 className="text-xl font-semibold">A special category: children & persons with disabilities</h3>
          <p className="mt-2 text-[15px] text-[#5C5F6B] leading-relaxed max-w-[760px]">
            The law sets a higher bar here, and it isn't optional: verifiable parental/guardian consent, confirmed
            relationship, no tracking or targeted ads, and nothing that could harm their well-being. The DP team checks all
            four before any of this data is collected.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export default DataHandling;
